import { useAuth } from 'react-oidc-context';
import { BasketContext } from '~/providers/basketContext';
import { BasketResponse } from '~/lib/types/basket-types';
import { useAnalytics, useTenant } from '@parcel2go/npm-p2g-app';
import { useCallback, useContext, useMemo, useState } from 'react';
import { BASKET_API } from '~/lib/constants';
import { authenticatedFetchWithTenantAnalytics } from '~/lib/fetch';
import { captureException } from '~/lib/metrics';

interface IP2GBasketSectionOptions {
  /**The name of the basket section */
  name: string;
}

/**
 * Encaptutes ways of working with a basket section e.g Saving, Loading and Modifying the data within a basket section.
 */
export type IP2GBasketSection<T = Record<string, string>> = {
  /** The data that is currently stored within the basket section, this is in memory and might not have being persited yet. */
  data: T;
  /** A boolean stating if the any type of syncing either saving or loading is currently taking place on this backet section. */
  loading: boolean;
  /** A boolean stating if the any part of the basket is being saved. */
  saving: boolean;
  /** A boolean stating if the basket is being requoted. */
  quoting: boolean;
  /** Update function used to modify the in memory version of the basket section. */
  update: (item: T) => void;
  /** Save function used to persit the in memory data with the basket API. */
  save: (requote?: boolean) => void;
  /** Load function used to force a refresh of the in memory data with that stored on the server, unsaved changes will be lost */
  load: () => void;
};

/**
 * A hook used to interact with a specific section of the basket, this will allow you to read, write and persist data to the basket API.
 */
export default function useBasketSection<T = Record<string, string>>({ name }: IP2GBasketSectionOptions) {
  const { tenant, settings } = useTenant();
  const { user } = useAuth();
  const { state, dispatch } = useContext(BasketContext);
  const [changed, setChanged] = useState(false);
  const { session } = useAnalytics();

  const loading = useMemo(() => {
    return !state.initialised || state.fetching || !state.basket.items[state.currentItem];
  }, [state.currentItem, state.basket.items, state.fetching, state.initialised]);

  const saving = useMemo(() => state.saving, [state.saving]);

  const quoting = useMemo(() => state.requote, [state.requote]);

  const item = useMemo(() => {
    if (!state.initialised) {
      return {};
    }

    if (!state.basket.items[state.currentItem]) {
      return {};
    }

    if (!state.basket.items[state.currentItem].sections[name]) {
      return {};
    }

    return state.basket.items[state.currentItem].sections[name].payload as T;
  }, [state.currentItem, name, state.basket.items, state.initialised]);

  const update = useCallback(
    (item: T) => {
      setChanged(true);
      dispatch({ type: 'ADD_CHANGES', changes: [name] });
      dispatch({ type: 'UPDATE_SECTION_PAYLOAD', id: state.currentItem, name: name, payload: item });
    },
    [dispatch, state.currentItem, name]
  );

  const save = useCallback(
    async (requote?: boolean) => {
      if (!changed) return;

      try {
        dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: true });
        const item = state.basket.items[state.currentItem].sections[name];

        const response = await authenticatedFetchWithTenantAnalytics(
          user,
          tenant,
          settings,
          session,
          `${BASKET_API}/${tenant}/basket/${state.basket.id}/${state.currentItem}/${name}`,
          {
            method: 'POST',
            body: JSON.stringify(item),
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to save section invalid http response code ${response.status}`);
        }

        const result = (await response.json()) as BasketResponse;
        if (!result?.success || !result.result?.items[state.currentItem]?.sections[name]) {
          throw new Error(`Failed to save section invalid api response ${result.error}`);
        }

        dispatch({
          type: 'UPDATE_SECTION',
          id: state.currentItem,
          name: name,
          section: result.result?.items[state.currentItem]?.sections[name]
        });

        if (requote) {
          dispatch({ type: 'REQUEST_REQUOTE', value: true });
        }

        state.events.emit('basket_section_saved', {
          id: state.currentItem,
          name: name,
          section: result.result?.items[state.currentItem]?.sections[name]
        });
        state.broadcast.postMessage({
          type: 'basket_section_saved',
          id: state.currentItem,
          section: name,
          payload: result.result?.items[state.currentItem]?.sections[name]
        });
        setChanged(false);

        dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: false });
      } catch (e) {
        state.events.emit('basket_section_save_failed', { id: state.currentItem, name: name, error: e });
        captureException(e, 'Error in useBasketSection save', {
          section: 'basket'
        });
      }
    },
    [
      changed,
      dispatch,
      state.basket.items,
      state.basket.id,
      state.currentItem,
      state.events,
      state.broadcast,
      name,
      user,
      tenant,
      settings,
      session
    ]
  );

  const load = useCallback(async () => {
    try {
      dispatch({ type: 'UPDATE_LOADING', fetching: true, saving: false });

      const response = await authenticatedFetchWithTenantAnalytics(
        user,
        tenant,
        settings,
        session,
        `${BASKET_API}/${tenant}/basket/${state.basket.id}/${state.currentItem}/${name}`
      );

      if (!response.ok) {
        throw new Error(`Failed to load section invalid http response code ${response.status}`);
      }

      const result = (await response.json()) as BasketResponse;
      if (!result?.success || !result.result?.items[state.currentItem]?.sections[name]) {
        throw new Error(`Failed to load section invalid api response ${result.error}`);
      }

      dispatch({
        type: 'UPDATE_SECTION',
        id: state.currentItem,
        name: name,
        section: result.result?.items[state.currentItem]?.sections[name]
      });
      setChanged(false);
      state.events.emit('basket_section_fetched', {
        id: state.currentItem,
        name: name,
        section: result.result?.items[state.currentItem]?.sections[name]
      });
      state.broadcast.postMessage({
        type: 'basket_section_fetched',
        id: state.currentItem,
        name: name,
        section: result.result?.items[state.currentItem]?.sections[name]
      });

      dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: false });
    } catch (e) {
      captureException(e, 'Error in useBasketSection load', {
        section: 'basket'
      });
    }
  }, [
    dispatch,
    user,
    tenant,
    settings,
    session,
    state.basket.id,
    state.currentItem,
    state.events,
    state.broadcast,
    name
  ]);

  return {
    data: item,
    loading: loading,
    saving: saving,
    quoting: quoting,
    update: update,
    save: save,
    load: load
  } as IP2GBasketSection<T>;
}
